/**
 * @file New Appointment Page — /nueva-cita
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, UserPlus, Search } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useAppointments } from "../hooks/useAppointments";
import { useAvailableSlots } from "../hooks/useAvailableSlots";
import { useBookingFlow } from "../hooks/useBookingFlow";
import { professionalService } from "../services/professional";
import { patientService } from "../services/patient";
import TimeSlotPicker from "../components/TimeSlotPicker";
import BookingFlow, { buildConfirmationData } from "../components/BookingFlow";
import NewPatientDialog from "../components/NewPatientDialog";
import {
  APPOINTMENT_STRINGS,
  PROFESSIONAL_STRINGS,
  SCHEDULE_STRINGS,
  COMMON_STRINGS,
} from "../constants/strings";
import {
  Button,
  Card,
  Checkbox,
  Input,
  Select,
  SelectItem,
  Spinner,
  Textarea,
  toast,
} from "../ui";
import "./NewAppointment.css";

function PatientSearch({ patients, value, onChange, onAddNew }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Patients may be flat PatientRecords (professional view) or a nested patient profile (self-booking).
  const pName = (p) =>
    (p?.user ? `${p.user.firstName} ${p.user.lastName || ""}` : `${p?.firstName || ""} ${p?.lastName || ""}`).trim();
  const pContact = (p) => (p?.user ? p.user.phone || p.user.email : p?.phone || p?.email) || "";

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return patients.slice(0, 20);
    return patients.filter((p) => pName(p).toLowerCase().includes(q) || pContact(p).toLowerCase().includes(q));
  }, [patients, query]);

  const display = value ? pName(value) : "";

  return (
    <div className="napt-search" ref={wrapRef}>
      <Input
        label={APPOINTMENT_STRINGS.PATIENT}
        placeholder={APPOINTMENT_STRINGS.PATIENT_SEARCH_PLACEHOLDER}
        value={open ? query : display}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        leftIcon={<Search size={14} />}
      />
      {open && (
        <div className="napt-search-pop">
          {filtered.length === 0 ? (
            <button
              type="button"
              className="napt-search-empty"
              onMouseDown={(e) => {
                e.preventDefault();
                setOpen(false);
                onAddNew();
              }}
            >
              + Registrar nuevo paciente
            </button>
          ) : (
            filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className="napt-search-item"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(p);
                  setQuery("");
                  setOpen(false);
                }}
              >
                <div>
                  <div className="napt-search-name">
                    {pName(p) || "Paciente"}
                  </div>
                  <div className="napt-search-meta">
                    {pContact(p) || "Sin contacto"}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function NewAppointment() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { createAppointment } = useAppointments({ autoFetch: false });

  const [professionals, setProfessionals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [professionalId, setProfessionalId] = useState("");
  const [patientId, setPatientId] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [reason, setReason] = useState("");
  const [forSomeoneElse, setForSomeoneElse] = useState(false);
  const [attendeeName, setAttendeeName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedDateSimple, setSelectedDateSimple] = useState("");
  const [selectedSlotSimple, setSelectedSlotSimple] = useState(null);

  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [isBookingPro, setIsBookingPro] = useState(false);

  const [newPatientOpen, setNewPatientOpen] = useState(false);

  const isProfessional = user.role === "PROFESIONAL";
  const selectedPro = professionals.find((p) => p.id === Number(professionalId));

  const booking = useBookingFlow({
    professional: isProfessional ? selectedPro || null : null,
    mode: "auth",
  });

  const { slots: simpleSlots, isLoading: simpleSlotsLoading } = useAvailableSlots(
    !isProfessional && professionalId ? Number(professionalId) : null,
    !isProfessional ? selectedDateSimple : ""
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        if (isProfessional) {
          const profRes = await professionalService.getMe();
          if (profRes.data) {
            setProfessionalId(profRes.data.id);
            setProfessionals([profRes.data]);
          }
        } else {
          const profRes = await professionalService.getAll();
          setProfessionals(profRes.data || []);
          const preselect = searchParams.get("profesional");
          if (preselect) setProfessionalId(preselect);
        }

        if (user.role === "PACIENTE") {
          const patRes = await patientService.getMe();
          if (patRes.data) {
            setPatientId(patRes.data.id);
            setPatients([patRes.data]);
          }
        } else {
          const patRes = await patientService.getAll();
          setPatients(patRes.data || []);
        }
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user.role, isProfessional]); // eslint-disable-line

  useEffect(() => {
    setSelectedSlotSimple(null);
  }, [professionalId, selectedDateSimple]);

  const _n = new Date();
  const today = `${_n.getFullYear()}-${String(_n.getMonth() + 1).padStart(2, "0")}-${String(_n.getDate()).padStart(2, "0")}`;

  const handleNewPatientCreated = (patient) => {
    setPatients((prev) => (prev.find((p) => p.id === patient.id) ? prev : [...prev, patient]));
    setPatientId(patient.id);
    setSelectedPatient(patient);
  };

  const handleBookingConfirm = async ({ slot, reason: bookingReason, attendeeName: bookingAttendeeName }) => {
    const confirmation = buildConfirmationData({
      professional: selectedPro,
      selectedLocation: booking.selectedLocation,
      selectedService: booking.selectedService,
      selectedDate: booking.selectedDate,
      slot,
      reason: bookingReason,
    });
    try {
      setIsBookingPro(true);
      const startDt = new Date(slot.dateTime);
      const duration = selectedPro?.sessionDuration || 60;
      const endDt = new Date(startDt.getTime() + duration * 60000);

      await createAppointment({
        professionalId: Number(professionalId),
        ...(isProfessional ? { patientRecordId: Number(patientId) } : { patientId: Number(patientId) }),
        dateTime: startDt.toISOString(),
        endTime: endDt.toISOString(),
        reason: bookingReason || undefined,
        ...(bookingAttendeeName ? { attendeeName: bookingAttendeeName } : {}),
        locationId: booking.selectedLocation?.id,
        serviceId: booking.selectedService?.id,
      });
      setConfirmedBooking(confirmation);
    } catch {
      // ignore
    } finally {
      setIsBookingPro(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!professionalId || !patientId || !selectedSlotSimple) return;
    try {
      setSubmitting(true);
      const startDt = new Date(selectedSlotSimple.dateTime);
      const duration = selectedPro?.sessionDuration || 60;
      const endDt = new Date(startDt.getTime() + duration * 60000);

      await createAppointment({
        professionalId: Number(professionalId),
        ...(isProfessional ? { patientRecordId: Number(patientId) } : { patientId: Number(patientId) }),
        dateTime: startDt.toISOString(),
        endTime: endDt.toISOString(),
        reason,
        ...(forSomeoneElse && attendeeName.trim() ? { attendeeName: attendeeName.trim() } : {}),
      });
      navigate("/citas");
    } catch {
      // ignore
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="napt-page">
        <div className="sk-block sk-h-200" />
      </div>
    );
  }

  const handleSelectPatient = (p, resetBooking = false) => {
    setSelectedPatient(p);
    setPatientId(p ? p.id : "");
    if (resetBooking) {
      setConfirmedBooking(null);
      booking.reset();
    }
  };

  const handleOpenNewPatient = () => {
    // NewPatientDialog resets its own form + phone-lookup state when `open`
    // flips true (see its useEffect), so just open it. (The old setters here
    // referenced state that was moved into the dialog and no longer exists.)
    setNewPatientOpen(true);
  };

  return (
    <div className="napt-page">
      <Button variant="ghost" leftIcon={<ArrowLeft size={14} />} onClick={() => navigate(-1)}>
        {COMMON_STRINGS.BACK}
      </Button>

      <header>
        <h1 className="napt-title">{APPOINTMENT_STRINGS.NEW_TITLE}</h1>
      </header>

      {isProfessional ? (
        <>
          <Card>
            <PatientSearch
              patients={patients}
              value={selectedPatient}
              onChange={(p) => handleSelectPatient(p, true)}
              onAddNew={handleOpenNewPatient}
            />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<UserPlus size={14} />}
              onClick={handleOpenNewPatient}
              style={{ marginTop: 8 }}
            >
              Registrar nuevo paciente
            </Button>
          </Card>

          {patientId && (
            <BookingFlow
              professional={selectedPro}
              booking={booking}
              onConfirm={handleBookingConfirm}
              isBooking={isBookingPro}
              confirmedBooking={confirmedBooking}
              confirmButtonLabel="Agendar cita"
              mode="professional"
            />
          )}
        </>
      ) : (
        <Card>
          <form noValidate onSubmit={handleSubmit} className="napt-form">
            <Select
              label={APPOINTMENT_STRINGS.PROFESSIONAL}
              value={professionalId ? String(professionalId) : ""}
              onValueChange={setProfessionalId}
              placeholder="Selecciona un profesional"
            >
              {professionals.map((prof) => (
                <SelectItem key={prof.id} value={String(prof.id)}>
                  {prof.user.firstName} {prof.user.lastName}
                  {prof.specialty
                    ? `, ${PROFESSIONAL_STRINGS.SPECIALTY_LABELS[prof.specialty] || prof.specialtyOther || ""}`
                    : ""}
                </SelectItem>
              ))}
            </Select>

            {user.role !== "PACIENTE" && (
              <>
                <PatientSearch
                  patients={patients}
                  value={selectedPatient}
                  onChange={(p) => handleSelectPatient(p)}
                  onAddNew={handleOpenNewPatient}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<UserPlus size={14} />}
                  onClick={handleOpenNewPatient}
                  style={{ alignSelf: "flex-start" }}
                >
                  Registrar nuevo paciente
                </Button>
              </>
            )}

            <Input
              type="date"
              label={APPOINTMENT_STRINGS.DATE_TIME}
              value={selectedDateSimple}
              onChange={(e) => setSelectedDateSimple(e.target.value)}
              min={today}
              disabled={!professionalId}
            />

            <div>
              <span className="napt-section-label">{SCHEDULE_STRINGS.SELECT_SLOT}</span>
              {professionalId && selectedDateSimple ? (
                <TimeSlotPicker
                  slots={simpleSlots}
                  selectedSlot={selectedSlotSimple}
                  onSelect={setSelectedSlotSimple}
                  isLoading={simpleSlotsLoading}
                />
              ) : (
                <p className="napt-hint">{APPOINTMENT_STRINGS.SELECT_DATE_FIRST}</p>
              )}
            </div>

            <Textarea
              label={APPOINTMENT_STRINGS.REASON}
              placeholder={APPOINTMENT_STRINGS.REASON_PLACEHOLDER}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />

            <div>
              <Checkbox
                label="La cita es para otra persona"
                checked={forSomeoneElse}
                onCheckedChange={(c) => {
                  setForSomeoneElse(!!c);
                  if (!c) setAttendeeName("");
                }}
              />
              {forSomeoneElse && (
                <Input
                  label="Nombre del paciente"
                  placeholder="Ej. Juan Hernández Jr."
                  value={attendeeName}
                  onChange={(e) => setAttendeeName(e.target.value)}
                  style={{ marginTop: 10 }}
                />
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={submitting}
              disabled={!selectedSlotSimple || !patientId}
            >
              {APPOINTMENT_STRINGS.CREATE}
            </Button>
          </form>
        </Card>
      )}

      <NewPatientDialog
        open={newPatientOpen}
        onClose={() => setNewPatientOpen(false)}
        onCreated={handleNewPatientCreated}
        showScheduleCTA={false}
      />
    </div>
  );
}
